import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, FileText, Download, Eye, Calendar, User, Filter, Upload, Plus, Trash2, X } from 'lucide-react';
import axios from 'axios';

const LearningMaterials = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const isLecturer = user?.role === 'lecturer';

  // Student state
  const [materials, setMaterials] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selectedUnit, setSelectedUnit] = useState('all');

  // Lecturer state
  const [assignedUnits, setAssignedUnits] = useState([]);
  const [selectedUnitForLecturer, setSelectedUnitForLecturer] = useState(null);
  const [materialsForLecturer, setMaterialsForLecturer] = useState([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);

  // Upload modal state
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [materialTitle, setMaterialTitle] = useState('');
  const [materialDescription, setMaterialDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isLecturer) {
      fetchAssignedUnits();
    } else {
      fetchMaterials();
    }
  }, []);

  useEffect(() => {
    // Check for unitId query parameter
    const unitId = searchParams.get('unitId');
    if (unitId) {
      if (isLecturer) {
        // For lecturer, find and select the unit
        const unit = assignedUnits.find(u => u.id.toString() === unitId);
        if (unit) {
          selectUnit(unit);
        }
      } else {
        setSelectedUnit(unitId);
      }
    }
  }, [searchParams, assignedUnits, isLecturer]);

  const fetchMaterials = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch learning materials for the student
      const materialsResponse = await axios.get('/learning-materials/student');

      if (materialsResponse.data) {
        setMaterials(materialsResponse.data);
      }
    } catch (error) {
      console.error('Error fetching materials:', error);
      setError('Failed to load learning materials. Please try again.');
      setMaterials([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignedUnits = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lecturers/${user.id}/assigned-units`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch assigned units');
      }

      const data = await response.json();
      // Filter for units where the lecturer has accepted the assignment OR it's a self-paced unit (status: active)
      const acceptedUnits = data.filter(unit => unit.assignmentStatus === 'accepted' || unit.assignmentStatus === 'active');
      setAssignedUnits(acceptedUnits);

      if (acceptedUnits.length > 0 && !selectedUnitForLecturer) {
        setSelectedUnitForLecturer(acceptedUnits[0]);
        fetchMaterialsForUnit(acceptedUnits[0].id);
      }
    } catch (error) {
      console.error('Error fetching assigned units:', error);
      setError('Failed to load assigned units. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchMaterialsForUnit = async (unitId) => {
    setLoadingMaterials(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/learning-materials/unit/${unitId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch materials');
      }

      const data = await response.json();
      setMaterialsForLecturer(data);
    } catch (error) {
      console.error('Error fetching materials:', error);
      setError('Failed to load materials for this unit.');
    } finally {
      setLoadingMaterials(false);
    }
  };

  const selectUnit = (unit) => {
    setSelectedUnitForLecturer(unit);
    fetchMaterialsForUnit(unit.id);
  };

  const pickDocument = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.doc,.docx,image/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setSelectedFile(file);
      }
    };
    input.click();
  };

  const uploadMaterial = async () => {
    if (!materialTitle.trim()) {
      alert('Please enter a title for the material.');
      return;
    }

    if (!selectedFile) {
      alert('Please select a file to upload.');
      return;
    }

    if (!selectedUnitForLecturer) {
      alert('No unit selected.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('title', materialTitle.trim());
      formData.append('description', materialDescription.trim());
      formData.append('file', selectedFile);
      formData.append('unitId', selectedUnitForLecturer.id);
      formData.append('lecturerId', user.id);

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/learning-materials`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        alert('Learning material uploaded successfully!');
        setUploadModalVisible(false);
        resetUploadForm();
        fetchMaterialsForUnit(selectedUnitForLecturer.id);
      } else {
        alert(data.message || 'Failed to upload material.');
      }
    } catch (error) {
      console.error('Error uploading material:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const deleteMaterial = async (materialId) => {
    // Use window.confirm instead of confirm to avoid ESLint error
    if (!window.confirm('Are you sure you want to delete this learning material? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/learning-materials/${materialId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        alert('Learning material deleted successfully.');
        fetchMaterialsForUnit(selectedUnitForLecturer.id);
      } else {
        alert('Failed to delete material.');
      }
    } catch (error) {
      console.error('Error deleting material:', error);
      alert('Delete failed. Please try again.');
    }
  };

  const resetUploadForm = () => {
    setMaterialTitle('');
    setMaterialDescription('');
    setSelectedFile(null);
  };

  const handleDownload = async (materialId, fileName) => {
    try {
      const response = await axios.get(`/learning-materials/${materialId}/download`, {
        responseType: 'blob' // Important for file downloads
      });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading material:', error);
      alert('Failed to download the material. Please try again.');
    }
  };

  const filteredMaterials = materials.filter(material => {
    if (filter === 'all') return true;
    if (filter === 'documents') return material.type === 'document' || material.fileType?.includes('pdf') || material.fileType?.includes('doc');
    if (filter === 'videos') return material.type === 'video' || material.fileType?.includes('mp4') || material.fileType?.includes('avi');
    if (filter === 'presentations') return material.type === 'presentation' || material.fileType?.includes('ppt');
    if (selectedUnit !== 'all') return material.unitId === selectedUnit;
    return true;
  });

  const getFileIcon = (type, fileType) => {
    if (type === 'video' || fileType?.includes('mp4') || fileType?.includes('avi')) {
      return '🎥';
    }
    if (type === 'presentation' || fileType?.includes('ppt')) {
      return '📊';
    }
    if (type === 'document' || fileType?.includes('pdf') || fileType?.includes('doc')) {
      return '📄';
    }
    return '📁';
  };

  const getFileSize = (sizeInBytes) => {
    if (!sizeInBytes) return 'Unknown';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(sizeInBytes) / Math.log(1024));
    return Math.round(sizeInBytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const MaterialCard = ({ material }) => (
    <div className="card hover:shadow-large transition-all duration-300">
      <div className="flex items-start space-x-4">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center text-2xl">
            {getFileIcon(material.type, material.fileType)}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate">
            {material.title}
          </h3>

          {material.description && (
            <p className="text-sm text-gray-600 mb-3 line-clamp-2">
              {material.description}
            </p>
          )}

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="flex items-center space-x-2">
              <BookOpen className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-700">
                {material.unit?.name || material.Unit?.name || 'Unit'}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <User className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-700">
                {material.lecturer?.name || material.Lecturer?.name || 'Course Material'}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-700">
                {new Date(material.createdAt).toLocaleDateString()}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <FileText className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-700">
                {getFileSize(material.fileSize)}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-2">
            <button
              className="btn-primary flex-1"
              onClick={() => handleDownload(material.id, material.fileName)}
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </button>
            <button className="btn-secondary">
              <Eye className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Get unique units for filter dropdown
  const uniqueUnits = [...new Set(materials.map(m => m.unitId))].filter(Boolean);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error-50 border border-error-200 rounded-lg p-6 text-center">
        <FileText className="w-12 h-12 text-error-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-error-800 mb-2">Error Loading Materials</h3>
        <p className="text-error-700 mb-4">{error}</p>
        <button
          onClick={fetchMaterials}
          className="btn-primary"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Lecturer Interface
  if (isLecturer) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">📚 Upload Learning Materials</h1>
            <p className="text-gray-600 mt-1">Share lecture notes, slides, and resources with your students</p>
          </div>
        </div>

        {/* Unit Selection */}
        {assignedUnits.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">📖 Select Unit</h2>
            <div className="flex flex-wrap gap-3">
              {assignedUnits.map((unit) => (
                <button
                  key={unit.id}
                  onClick={() => selectUnit(unit)}
                  className={`px-4 py-2 rounded-lg border transition-colors ${selectedUnitForLecturer?.id === unit.id
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                >
                  {unit.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Materials Section */}
        {selectedUnitForLecturer && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-gray-900">
                📄 Materials for {selectedUnitForLecturer.name}
              </h2>
              <button
                onClick={() => setUploadModalVisible(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Upload New
              </button>
            </div>

            {loadingMaterials ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Loading materials...</span>
              </div>
            ) : materialsForLecturer.length > 0 ? (
              <div className="space-y-4">
                {materialsForLecturer.map((material) => (
                  <div key={material.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex items-start space-x-4">
                        <div className="text-2xl">
                          {getFileIcon(material.type, material.fileType)}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{material.title}</h3>
                          {material.description && (
                            <p className="text-sm text-gray-600 mt-1">{material.description}</p>
                          )}
                          <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                            <span>📏 {getFileSize(material.fileSize)}</span>
                            <span>📅 {new Date(material.createdAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => deleteMaterial(material.id)}
                        className="text-red-600 hover:text-red-800 p-2"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No materials uploaded</h3>
                <p className="text-gray-600">Upload your first learning material to get started</p>
              </div>
            )}
          </div>
        )}

        {/* Upload Modal */}
        {uploadModalVisible && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Upload Learning Material</h3>
                <button
                  onClick={() => setUploadModalVisible(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={materialTitle}
                    onChange={(e) => setMaterialTitle(e.target.value)}
                    placeholder="Material title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    value={materialDescription}
                    onChange={(e) => setMaterialDescription(e.target.value)}
                    placeholder="Optional description"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    File
                  </label>
                  <button
                    onClick={pickDocument}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {selectedFile ? selectedFile.name : '📎 Select File (PDF, DOC, DOCX, Images)'}
                  </button>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setUploadModalVisible(false)}
                    className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={uploadMaterial}
                    disabled={uploading}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {uploading ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Student Interface
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Learning Materials</h1>
          <p className="text-gray-600 mt-1">Access course materials, documents, and resources</p>
        </div>

        {/* Filters */}
        <div className="flex space-x-2 mt-4 sm:mt-0">
          {/* Type Filter */}
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="form-input"
          >
            <option value="all">All Types</option>
            <option value="documents">Documents</option>
            <option value="videos">Videos</option>
            <option value="presentations">Presentations</option>
          </select>

          {/* Unit Filter */}
          <select
            value={selectedUnit}
            onChange={(e) => setSelectedUnit(e.target.value)}
            className="form-input"
          >
            <option value="all">All Units</option>
            {uniqueUnits.map(unitId => {
              const material = materials.find(m => m.unitId === unitId);
              const unitName = material?.unit?.name || material?.Unit?.name || `Unit ${unitId}`;
              return (
                <option key={unitId} value={unitId}>
                  {unitName}
                </option>
              );
            })}
          </select>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Total Materials</p>
              <p className="text-2xl font-bold">{materials.length}</p>
            </div>
            <BookOpen className="w-8 h-8 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Documents</p>
              <p className="text-2xl font-bold">
                {materials.filter(m => m.type === 'document' || m.fileType?.includes('pdf') || m.fileType?.includes('doc')).length}
              </p>
            </div>
            <FileText className="w-8 h-8 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Videos</p>
              <p className="text-2xl font-bold">
                {materials.filter(m => m.type === 'video' || m.fileType?.includes('mp4') || m.fileType?.includes('avi')).length}
              </p>
            </div>
            <span className="text-2xl">🎥</span>
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Presentations</p>
              <p className="text-2xl font-bold">
                {materials.filter(m => m.type === 'presentation' || m.fileType?.includes('ppt')).length}
              </p>
            </div>
            <span className="text-2xl">📊</span>
          </div>
        </div>
      </div>

      {/* Materials Grid */}
      {filteredMaterials.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredMaterials.map((material) => (
            <MaterialCard key={material.id} material={material} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No materials found</h3>
          <p className="text-gray-600">
            {filter === 'all' && selectedUnit === 'all'
              ? "No learning materials are available yet."
              : `No materials match the selected filters.`
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default LearningMaterials;
